import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	useCompleteSetup,
	useOrg,
	useSession,
	useSetupQuote,
	useUpdateName,
} from "@/hooks";
import { cycleNoun, formatMoney } from "@/lib/money";
import { ApiError } from "@/services";
import type { SetupQuote } from "@/types/api";
import { Check, ExternalLink } from "lucide-react";
import { useState } from "react";

/**
 * What a partner owner sees the first time they sign in.
 *
 * Staff create the organization with their email as owner; everything after
 * that is theirs to choose. Until this is completed the org has no contract
 * start, and the billing sweep skips orgs without one — so an org that is never
 * set up is never billed, rather than quietly accruing invoices nobody agreed
 * to.
 */
export function SetupPage() {
	const org = useOrg();
	const session = useSession();
	const complete = useCompleteSetup();
	const updateName = useUpdateName();

	// Prefilled from the session, which is DB-backed rather than read off the
	// access token — a name saved here would otherwise appear to revert until
	// the token next rotated.
	const [name, setName] = useState<string | null>(null);
	const knownName = session.data?.name ?? "";
	const nameValue = name ?? knownName;

	// Prefilled from what the org already has, else the signed-in owner's own
	// address — the person filling this in is usually the person invoices should
	// reach, and a wrong default is easier to correct than a blank field is to
	// fill.
	const [billingEmail, setBillingEmail] = useState<string | null>(null);
	const knownBilling =
		org.data?.org.billing_email ?? session.data?.email ?? "";
	const billingValue = billingEmail ?? knownBilling;

	const [seats, setSeats] = useState(10);
	const [code, setCode] = useState("");
	const [applied, setApplied] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState<{
		quote: SetupQuote;
		paymentUrl: string | null;
	} | null>(null);

	const quoteQ = useSetupQuote(seats, applied, !done);
	const quote = quoteQ.data;
	const per = cycleNoun(quote?.cycle);

	if (org.isLoading) return <Skeleton className="h-96 w-full" />;

	// Only an owner can commit the organization to money. An admin arriving
	// first should be told what is missing, not shown a form that will 403.
	if (org.data?.org.role !== "owner") {
		return (
			<Card>
				<CardContent className="p-6">
					<h1 className="text-title2 font-semibold text-heading">
						Almost ready
					</h1>
					<p className="mt-2 max-w-md text-body text-muted-foreground">
						Your organization's owner needs to choose a seat count before the
						team can start. Once they have, this becomes your dashboard.
					</p>
				</CardContent>
			</Card>
		);
	}

	if (done) {
		return (
			<Card>
				<CardContent className="flex flex-col gap-4 p-6">
					<div className="flex items-center gap-2">
						<Check className="size-5 text-success" />
						<h1 className="text-title2 font-semibold text-heading">
							You're set up
						</h1>
					</div>
					{/*
					 * Says the seats are usable NOW. Otherwise an admin waits for a
					 * payment to clear before inviting anyone, and the first day of
					 * the pilot is spent doing nothing.
					 */}
					<p className="max-w-lg text-body text-muted-foreground">
						{done.quote.seats} seats are live — you can invite your team right
						away. An invoice for{" "}
						<span className="font-medium text-heading">
							{formatMoney(done.quote.total_cents, done.quote.currency)}
						</span>{" "}
						has been sent to your billing contact.
					</p>
					<div className="flex gap-2">
						{done.paymentUrl ? (
							<Button asChild variant="secondary">
								<a href={done.paymentUrl} target="_blank" rel="noreferrer">
									Pay now <ExternalLink className="ml-1.5 size-3.5" />
								</a>
							</Button>
						) : null}
						<Button onClick={() => location.assign("/seats")}>
							Invite your team
						</Button>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className="flex flex-col gap-6 p-6">
				<div>
					<h1 className="text-title2 font-semibold text-heading">
						Set up {org.data?.org.name}
					</h1>
					<p className="mt-1 max-w-lg text-body text-muted-foreground">
						Choose how many people you're starting with. You can add more at any
						time, and removing someone frees their seat for the rest of the{" "}
						{per} at no extra cost.
					</p>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="name" className="text-body font-medium text-heading">
						Your name
					</label>
					<Input
						id="name"
						value={nameValue}
						onChange={(e) => setName(e.target.value)}
						placeholder="How you'll appear to your team"
						className="max-w-xs"
						autoComplete="name"
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label
						htmlFor="billing-email"
						className="text-body font-medium text-heading"
					>
						Billing email
					</label>
					<Input
						id="billing-email"
						value={billingValue}
						onChange={(e) => setBillingEmail(e.target.value)}
						placeholder="finance@yourcompany.com"
						className="max-w-xs"
						autoComplete="email"
					/>
					<p className="text-caption text-muted-foreground">
						Where your invoices are sent. Often finance rather than you.
					</p>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="seats" className="text-body font-medium text-heading">
						Seats
					</label>
					<Input
						id="seats"
						type="number"
						min={1}
						className="w-28 tabular"
						value={seats}
						onChange={(e) => {
							const n = Number.parseInt(e.target.value, 10);
							setSeats(Number.isNaN(n) ? 0 : n);
						}}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="code" className="text-body font-medium text-heading">
						Discount code
					</label>
					<div className="flex gap-2">
						<Input
							id="code"
							value={code}
							onChange={(e) => setCode(e.target.value.toUpperCase())}
							onKeyDown={(e) => e.key === "Enter" && setApplied(code.trim())}
							placeholder="If you were given one"
							className="max-w-xs"
						/>
						<Button
							variant="outline"
							disabled={!code.trim() || code.trim() === applied}
							onClick={() => setApplied(code.trim())}
						>
							Apply
						</Button>
					</div>
					{quote?.discount_error ? (
						<p className="text-caption text-error">{quote.discount_error}</p>
					) : quote?.discount_code ? (
						<p className="text-caption text-success">
							{quote.discount_code} applied.
						</p>
					) : null}
				</div>

				{seats < 1 ? (
					<p className="text-caption text-muted-foreground">
						Enter how many seats you need.
					</p>
				) : quoteQ.isLoading ? (
					<Skeleton className="h-28 w-full" />
				) : quote ? (
					<Breakdown quote={quote} per={per} />
				) : null}

				{error ? <p className="text-caption text-error">{error}</p> : null}

				<div>
					<Button
						disabled={
							!quote ||
							complete.isPending ||
							seats < 1 ||
							!/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(billingValue.trim())
						}
						onClick={async () => {
							setError(null);
							try {
								// Saved first, but never allowed to stop the contract:
								// a rejected name must not cost the owner their setup.
								const trimmed = nameValue.trim();
								if (trimmed && trimmed !== knownName) {
									await updateName.mutateAsync(trimmed).catch(() => {});
								}
								const r = await complete.mutateAsync({
									seats,
									code: applied || undefined,
									billingEmail: billingValue.trim() || undefined,
								});
								setDone({
									quote: r.quote,
									paymentUrl: r.invoice.payment_url,
								});
							} catch (e) {
								setError(
									e instanceof ApiError
										? e.message
										: "Could not complete setup. Nothing has been charged.",
								);
							}
						}}
					>
						{complete.isPending
							? "Setting up…"
							: quote
								? `Start with ${seats} seats — ${formatMoney(quote.total_cents, quote.currency)}`
								: "Start"}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

/** Every number the first invoice will carry, and nothing it will not. */
function Breakdown({ quote, per }: { quote: SetupQuote; per: string }) {
	const c = quote.currency;
	return (
		<div className="rounded-md border border-divider bg-surface2 p-4">
			<Row
				label={`${quote.seats} × seat, per ${per}`}
				value={`${formatMoney(quote.unit_price_cents ?? 0, c)} each`}
			/>
			{quote.discount_cents > 0 ? (
				<>
					<Row label="Subtotal" value={formatMoney(quote.subtotal_cents, c)} />
					<Row
						label={`Discount (${quote.discount_code})`}
						value={`− ${formatMoney(quote.discount_cents, c)}`}
					/>
				</>
			) : null}
			<div className="my-2.5 h-px bg-divider" />
			<div className="flex items-baseline justify-between">
				<span className="text-body font-medium text-heading">
					Due per {per}
				</span>
				<span className="tabular text-title2 font-semibold text-heading">
					{formatMoney(quote.total_cents, c)}
				</span>
			</div>
			{/* The rate moves with the seat count, so an admin one seat short of a
			    cheaper bracket should be able to see that for themselves. */}
			<p className="mt-2 text-caption text-muted-foreground">
				The per-seat rate improves as your team grows — try a larger number to
				see the next bracket.
			</p>
		</div>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-baseline justify-between gap-4">
			<span className="text-caption text-muted-foreground">{label}</span>
			<span className="tabular text-body text-heading">{value}</span>
		</div>
	);
}
